
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getUsersWhoLikedVideo } from '@/services/likesService';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useNavigate } from 'react-router-dom';

export interface LikesModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoId: string | number | null;
}

interface UserWhoLiked {
  id: string;
  name: string;
  profileId: string | null;
}

const LikesModal: React.FC<LikesModalProps> = ({ isOpen, onClose, videoId }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<UserWhoLiked[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      if (!videoId) return;
      
      setIsLoading(true);
      try {
        // Convert videoId to number if it's a string
        const numericId = typeof videoId === 'string' ? parseInt(videoId, 10) : videoId;
        
        if (!isNaN(numericId)) {
          const data = await getUsersWhoLikedVideo(numericId);
          setUsers(data);
        }
      } catch (error) {
        console.error('Error fetching users who liked the video:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && videoId) {
      fetchUsers();
    }
  }, [isOpen, videoId]);
  
  const handleProfileClick = (userId: string) => {
    onClose();
    navigate(`/profile/${userId}`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Liked by</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-72 w-full pr-4">
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-4">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <Skeleton className="h-4 w-[250px]" />
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No likes yet</p>
          ) : (
            <div className="space-y-4">
              {users.map(user => (
                <div 
                  key={user.id}
                  className="flex items-center space-x-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md cursor-pointer"
                  onClick={() => handleProfileClick(user.id)}
                >
                  <Avatar>
                    <AvatarFallback>{user.name?.charAt(0) || 'U'}</AvatarFallback>
                    <AvatarImage src={`/api/avatar/${user.id}`} />
                  </Avatar>
                  <div>
                    <p className="font-medium">{user.name}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default LikesModal;
